import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicy,
})

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <main className="mx-auto max-w-3xl px-4 py-12">
        <header className="mb-8">
          <h1 className="font-semibold text-3xl tracking-tight">Privacy Policy</h1>
          <p className="text-slate-600">
            for <strong>OpenIron</strong>
          </p>
          <p className="text-slate-500 text-sm">Last updated: October 29, 2025</p>
        </header>

        <section aria-labelledby="overview" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="overview" className="mb-2 font-semibold text-lg">
            1) Overview
          </h2>
          <p>
            We respect your privacy. This policy explains what information we collect, how we use it, and your choices.
            OpenIron does <strong>not</strong> use advertising or analytics trackers. We use only{" "}
            <strong>Sentry</strong> for error monitoring and <strong>Google</strong> for OAuth sign-in.
          </p>
        </section>

        <section aria-labelledby="data-we-collect" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="data-we-collect" className="mb-2 font-semibold text-lg">
            2) Information We Collect
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Account basics (via Google OAuth):</strong> When you sign in with Google, we receive your name,
              email address, and Google profile ID as provided by Google. We do not access your emails, files, or
              contacts.
            </li>
            <li>
              <strong>App data you provide:</strong> Workout entries, preferences, and other content you add in the app.
            </li>
            <li>
              <strong>Diagnostics (via Sentry):</strong> On errors or crashes we collect technical logs such as
              timestamps, app version, device/OS information, and error details; Sentry may also capture IP address as
              part of standard internet requests.
            </li>
          </ul>
        </section>

        <section aria-labelledby="how-we-use" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="how-we-use" className="mb-2 font-semibold text-lg">
            3) How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>To provide and maintain the app and your account (sign-in, sync, basic functionality).</li>
            <li>To diagnose, fix, and improve reliability using Sentry error reports.</li>
            <li>To communicate about critical changes, security alerts, or service issues.</li>
          </ul>
        </section>

        <section aria-labelledby="no-selling" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="no-selling" className="mb-2 font-semibold text-lg">
            4) No Selling or Sharing
          </h2>
          <p>
            We do <strong>not</strong> sell your personal information. We do <strong>not</strong> share it with third
            parties for advertising or marketing. Service providers we use (e.g., Sentry for error monitoring) only
            process data on our behalf under contractual terms.
          </p>
        </section>

        <section aria-labelledby="oauth" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="oauth" className="mb-2 font-semibold text-lg">
            5) Google OAuth Sign-In
          </h2>
          <p>
            We use Google as an identity provider. Using Google OAuth lets you sign in without a separate password. You
            can revoke our access at any time from your Google Account settings. We use the information only to
            authenticate you and operate your account.
          </p>
        </section>

        <section aria-labelledby="sentry" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="sentry" className="mb-2 font-semibold text-lg">
            6) Error Monitoring (Sentry)
          </h2>
          <p>
            We use Sentry solely to detect, investigate, and fix bugs. Diagnostic data is limited to what is necessary
            for this purpose and is retained only as long as needed, subject to Sentry's retention settings and
            policies. Where possible, we avoid including personal content in error logs.
          </p>
        </section>

        <section aria-labelledby="retention" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="retention" className="mb-2 font-semibold text-lg">
            7) Data Retention
          </h2>
          <p>
            We keep your account and app data for as long as your account is active or as needed to provide the service.
            Diagnostic data from Sentry is retained for limited periods consistent with our operational needs.
          </p>
        </section>

        <section aria-labelledby="rights" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="rights" className="mb-2 font-semibold text-lg">
            8) Your Rights &amp; Choices
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Access, update, delete:</strong> You can request access to, correction of, or deletion of your
              personal information.
            </li>
            <li>
              <strong>Opt-out of email:</strong> You can opt out of non-essential emails (if any). We may still send
              critical service or security notices.
            </li>
            <li>
              <strong>Revoke sign-in:</strong> You may disconnect Google OAuth from your Google Account settings.
            </li>
          </ul>
          <p>
            To make a request, contact us at{"  "}
            <a href="mailto:austin21wilcox@gmail.com" className="text-blue-600 underline">
              austin21wilcox@gmail.com
            </a>
            .
          </p>
        </section>

        <section aria-labelledby="security" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="security" className="mb-2 font-semibold text-lg">
            9) Security
          </h2>
          <p>
            We use reasonable administrative, technical, and organizational measures to protect your information. No
            method of transmission or storage is 100% secure.
          </p>
        </section>

        <section aria-labelledby="children" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="children" className="mb-2 font-semibold text-lg">
            10) Children's Privacy
          </h2>
          <p>
            The service is not directed to children under 13 (or the minimum age required in your country). We do not
            knowingly collect personal information from children.
          </p>
        </section>

        <section aria-labelledby="intl" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="intl" className="mb-2 font-semibold text-lg">
            11) International Transfers
          </h2>
          <p>
            Your information may be processed in countries other than your own. We take steps to ensure appropriate
            safeguards when data is transferred.
          </p>
        </section>

        <section aria-labelledby="changes" className="mb-4 rounded-2xl bg-slate-50 p-5">
          <h2 id="changes" className="mb-2 font-semibold text-lg">
            12) Changes to This Policy
          </h2>
          <p>
            We may update this policy from time to time. If we make material changes, we will notify you within the app
            or by email.
          </p>
        </section>

        <section aria-labelledby="contact" className="rounded-2xl bg-slate-50 p-5">
          <h2 id="contact" className="mb-2 font-semibold text-lg">
            13) Contact Us
          </h2>
          <p>
            Questions or requests? Email us at{"  "}
            <a href="mailto:austin21wilcox@gmail.com" className="text-blue-600 underline">
              austin21wilcox@gmail.com
            </a>
            .
          </p>
        </section>

        <p className="mt-6 text-slate-500 text-sm">
          This template is provided for informational purposes only and does not constitute legal advice.
        </p>
      </main>
    </div>
  )
}
